import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useParams } from "react-router-dom";
import { tr } from "../i18n";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: BASE, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config || {};
    if (err.response?.status === 401 && !orig._retried && !(orig.url || "").includes("/auth/")) {
      orig._retried = true;
      try {
        await axios.post(`${BASE}/auth/refresh`, {}, { withCredentials: true });
        return api(orig);
      } catch {
        /* fall through */
      }
    }
    return Promise.reject(err);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return tr("Something went wrong. Please try again.");
  if (typeof d === "string") return tr(d);
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return String(d);
}

const AuthContext = createContext({ user: undefined });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);
  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    setUser(null);
  };
  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      /* noop */
    }
  };
  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function ProtectedRoute({ role, children }) {
  const { user } = useAuth();
  const { lang = "en" } = useParams();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-esaia-canvas" data-testid="auth-loading">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-esaia-subtle">Loading</p>
      </div>
    );
  }
  if (!user) return <Navigate to={`/${lang}/login`} replace />;
  if (user.must_change_password) return <Navigate to={`/${lang}/change-password`} replace />;
  if (role && user.role !== role) {
    return <Navigate to={`/${lang}/${user.role === "admin" ? "admin" : "portal"}`} replace />;
  }
  return children;
}
